import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowForward as ArrowForwardIcon,
  FactoryOutlined as FactoryIcon,
  LockOutlined as LockIcon,
  PersonOutline as PersonIcon,
  SecurityOutlined as SecurityIcon,
  Visibility,
  VisibilityOff,
  VisibilityOutlined as VisibilityIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import { useAuthStore } from '../store/authStore';
import loginBackground from '../Images/login_background.jpg';
import oceanLankaLogo from '../Images/ocl-logo-no-background.png';

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const setAuth = useAuthStore((state) => state.setAuth);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authService.login({ username, password });
      setAuth(response.user, response.token);
      navigate('/');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.18fr) minmax(420px, 0.82fr)' },
        bgcolor: '#f5f8fb',
        '@media (max-width: 899.95px)': {
          backgroundImage: `linear-gradient(rgba(2, 34, 52, 0.68), rgba(0, 45, 65, 0.76)), url(${loginBackground})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
        },
      }}
    >
      <Box
        component="img"
        src={oceanLankaLogo}
        alt="Ocean Lanka"
        sx={{
          position: 'absolute',
          zIndex: 5,
          top: { xs: 12, md: 20 },
          right: { xs: 18, md: 28 },
          width: { xs: 88, sm: 112, md: 132 },
          height: 'auto',
          borderRadius: 1,
          bgcolor: '#fff',
          p: 0.5,
          filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.22))',
        }}
      />
      <Box
        component="section"
        aria-label="Ocean Lanka production operations"
        sx={{
          position: 'relative',
          minHeight: { xs: 320, sm: 390, md: '100vh' },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          overflow: 'hidden',
          color: '#fff',
          backgroundImage: `linear-gradient(112deg, rgba(2, 34, 52, 0.55), rgba(0, 45, 65, 0.38) 58%, rgba(0, 34, 52, 0.5)), url(${loginBackground})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          '@media (max-width: 899.95px)': {
            minHeight: 0,
            backgroundImage: 'none',
            color: '#fff',
            overflow: 'visible',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            background: 'linear-gradient(135deg, transparent 70%, rgba(0, 211, 199, 0.12))',
            '@media (max-width: 899.95px)': { display: 'none' },
          },
        }}
      >
        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            px: { xs: 2.5, sm: 5, lg: 7 },
            pt: { xs: 10, md: 4 },
            '@media (max-width: 899.95px)': { textAlign: 'center', pb: 2 },
          }}
        >
          <Typography
            component="h1"
            sx={{
              fontSize: { xs: 48, sm: 58, md: 68 },
              fontWeight: 900,
              letterSpacing: '-0.06em',
              lineHeight: 1,
              color: '#fff',
              '@media (max-width: 899.95px)': { textShadow: '0 2px 10px rgba(0,0,0,0.3)' },
            }}
          >
            Smart<Box component="span" sx={{ color: '#1fd2c4' }}>MES</Box>
          </Typography>
          <Typography
            sx={{
              mt: 1,
              fontSize: { xs: 9, sm: 11 },
              fontWeight: 700,
              letterSpacing: { xs: '0.14em', sm: '0.22em' },
              color: 'rgba(255,255,255,0.92)',
              '@media (max-width: 899.95px)': { textShadow: '0 1px 5px rgba(0,0,0,0.35)' },
            }}
          >
            OCEAN LANKA · PRINTING DEPARTMENT
          </Typography>
        </Box>

        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            px: { xs: 3, sm: 5, lg: 7 },
            py: { xs: 4, md: 8 },
            '@media (max-width: 899.95px)': { display: 'none' },
          }}
        >
          <Typography
            component="h1"
            sx={{
              maxWidth: 560,
              fontSize: { xs: 32, sm: 42, lg: 48 },
              fontWeight: 800,
              letterSpacing: '-0.035em',
              lineHeight: 1.12,
            }}
          >
            Smarter Production.
            <Box component="span" sx={{ display: 'block', color: '#1fd2c4' }}>
              Better Prints.
            </Box>
          </Typography>
          <Box sx={{ width: 36, height: 3, bgcolor: '#1fd2c4', my: 2 }} />
          <Typography sx={{ maxWidth: 440, fontSize: { xs: 14, sm: 16 }, lineHeight: 1.55, color: 'rgba(255,255,255,0.96)' }}>
            Real-time visibility across textile printing operations.
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              maxWidth: 520,
              mt: { xs: 3, sm: 5 },
            }}
          >
            {[
              { icon: <FactoryIcon />, label: <>Live Production<br />Tracking</> },
              { icon: <SecurityIcon />, label: <>Quality &amp; Defect<br />Control</> },
              { icon: <VisibilityIcon />, label: <>Shop Floor<br />Visibility</> },
            ].map((feature, index) => (
              <Box
                key={index}
                sx={{
                  px: { xs: 1, sm: 2 },
                  pl: index === 0 ? 0 : { xs: 1.5, sm: 2.5 },
                  borderLeft: index === 0 ? 'none' : '1px solid rgba(31, 210, 196, 0.65)',
                }}
              >
                <Box sx={{ color: '#1fd2c4', '& svg': { fontSize: 30 } }}>{feature.icon}</Box>
                <Typography sx={{ mt: 0.8, fontSize: { xs: 10, sm: 12 }, lineHeight: 1.35, color: 'rgba(255,255,255,0.96)' }}>
                  {feature.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Box
        component="main"
        sx={{
          minHeight: { xs: 'auto', md: '100vh' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 2.5, sm: 5, lg: 7 },
          py: { xs: 4, md: 6 },
          background: 'linear-gradient(145deg, #fbfdff 0%, #f2f6fa 100%)',
          '@media (max-width: 899.95px)': { pt: 2, alignItems: 'flex-start', bgcolor: 'transparent', background: 'transparent' },
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 450,
            '@media (max-width: 899.95px)': {
              p: { xs: 2, sm: 3 },
              borderRadius: 2,
              bgcolor: 'rgba(255,255,255,0.96)',
              boxShadow: '0 12px 32px rgba(0,0,0,0.2)',
            },
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5} sx={{ mb: 1.5 }}>
            <Box sx={{ width: 26, height: 2, bgcolor: '#008f91' }} />
            <Typography sx={{ color: '#008f91', fontSize: 10, fontWeight: 700, letterSpacing: '0.26em' }}>
              SECURE WORKSPACE
            </Typography>
          </Box>

          <Typography component="h2" sx={{ color: '#102335', fontSize: { xs: 32, sm: 36 }, fontWeight: 800, letterSpacing: '-0.04em' }}>
            Welcome back
          </Typography>
          <Typography sx={{ mt: 0.8, mb: 3.5, color: '#71849a', fontSize: 14 }}>
            Sign in to access your SmartMES account.
          </Typography>

          <Paper
            component="form"
            onSubmit={handleSubmit}
            elevation={0}
            sx={{ bgcolor: 'transparent' }}
          >
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Typography component="label" htmlFor="login-username" sx={{ display: 'block', mb: 0.8, color: '#17334b', fontSize: 13, fontWeight: 700 }}>
              Username / Employee ID
            </Typography>
            <TextField
              id="login-username"
              fullWidth
              placeholder="Enter your username or employee ID"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoFocus
              autoComplete="username"
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon sx={{ color: '#8195aa', fontSize: 21 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 2.8,
                '& .MuiOutlinedInput-root': {
                  height: 50,
                  borderRadius: 1.5,
                  bgcolor: 'rgba(255,255,255,0.75)',
                  '& fieldset': { borderColor: '#cbd7e2' },
                  '&:hover fieldset': { borderColor: '#90a9bc' },
                  '&.Mui-focused fieldset': { borderColor: '#008f91' },
                },
              }}
            />

            <Typography component="label" htmlFor="login-password" sx={{ display: 'block', mb: 0.8, color: '#17334b', fontSize: 13, fontWeight: 700 }}>
              Password
            </Typography>
            <TextField
              id="login-password"
              fullWidth
              placeholder="Enter your password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon sx={{ color: '#8195aa', fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      disabled={loading}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: 50,
                  borderRadius: 1.5,
                  bgcolor: 'rgba(255,255,255,0.75)',
                  '& fieldset': { borderColor: '#cbd7e2' },
                  '&:hover fieldset': { borderColor: '#90a9bc' },
                  '&.Mui-focused fieldset': { borderColor: '#008f91' },
                },
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              endIcon={!loading && <ArrowForwardIcon />}
              sx={{
                mt: 3.2,
                height: 50,
                borderRadius: 1.5,
                bgcolor: '#00877f',
                fontSize: 14,
                fontWeight: 700,
                textTransform: 'none',
                boxShadow: '0 5px 14px rgba(0, 135, 127, 0.18)',
                '&:hover': { bgcolor: '#00736d', boxShadow: '0 7px 18px rgba(0, 115, 109, 0.24)' },
              }}
            >
              {loading ? <CircularProgress size={23} sx={{ color: '#fff' }} /> : 'Sign In'}
            </Button>
          </Paper>

          <Box sx={{ mt: { xs: 3, md: 6 }, pt: { xs: 2, md: 3.5 }, borderTop: '1px solid #dbe3eb' }}>
            <Typography sx={{ color: '#71849a', fontSize: 11 }}>
              Need access? Contact your system administrator.
            </Typography>
            <Typography sx={{ mt: 1.2, color: '#8799ab', fontSize: 10 }}>
              © {new Date().getFullYear()} SmartMES&nbsp; · &nbsp;Secure enterprise access
            </Typography>
            <Typography sx={{ mt: 1.5, color: '#526a7c', fontSize: 10, fontWeight: 700, letterSpacing: '0.12em' }}>
              DEVELOPED BY SASIRI CHATHURIKA
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;